import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-course-player',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './course-player.component.html',
  styleUrls: ['./course-player.component.scss']
})
export class CoursePlayerComponent implements OnInit {
  courseId: number = 0;
  videoUrl: SafeResourceUrl | null = null;
  currentLesson: number = 1;
  showLessonList: boolean = false;
  
  // Mock data
  course = {
    id: 1,
    title: 'Lorem ipsum dolor sit amet consectetur',
    description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras mattis consectetur purus sit amet fermentum. Cras justo odio, dapibus ac facilisis in, egestas eget quam. Morbi leo risus, porta ac consectetur ac, vestibulum at eros.',
    image: 'assets/images/course-placeholder.jpg',
    instructor: 'John Doe',
    instructorTitle: 'Senior Instructor',
    instructorAvatar: 'assets/images/avatar-placeholder.jpg',
    duration: '6h 30min',
    lessonsCount: 12,
    rating: 4.7,
    ratingCount: 256,
    completed: false,
    progress: 35,
    categories: ['Categoría 01', 'Categoría 02'],
    isFree: false,
    price: 14.99,
    isPurchased: true,
    lessons: [
      {
        id: 1,
        title: 'Introducción al curso',
        duration: '15min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        completed: true,
        description: 'Descripción general del curso y sus objetivos principales.'
      },
      {
        id: 2,
        title: 'Conceptos básicos - Parte 1',
        duration: '25min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        completed: true,
        description: 'Comprensión de los fundamentos del tema.'
      },
      {
        id: 3,
        title: 'Conceptos básicos - Parte 2',
        duration: '30min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        completed: true,
        description: 'Continuación de los fundamentos del tema y ejemplos prácticos.'
      },
      {
        id: 4,
        title: 'Aplicaciones prácticas',
        duration: '40min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        completed: false,
        description: 'Casos de uso reales y aplicaciones prácticas.'
      },
      {
        id: 5,
        title: 'Desarrollo avanzado - Parte 1',
        duration: '35min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        completed: false,
        description: 'Exploración de conceptos avanzados del tema.'
      },
      {
        id: 6,
        title: 'Desarrollo avanzado - Parte 2',
        duration: '45min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        completed: false,
        description: 'Continuación de los conceptos avanzados y ejemplos de implementación.'
      },
      {
        id: 7,
        title: 'Estudio de casos',
        duration: '50min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        completed: false,
        description: 'Análisis de casos de éxito y mejores prácticas.'
      },
      {
        id: 8,
        title: 'Implementación en entornos reales',
        duration: '55min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        completed: false,
        description: 'Estrategias para implementar lo aprendido en situaciones reales.'
      },
      {
        id: 9,
        title: 'Conclusiones y próximos pasos',
        duration: '30min',
        videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        completed: false,
        description: 'Resumen del curso y recomendaciones para continuar el aprendizaje.'
      }
    ]
  };

  relatedCourses = [
    {
      id: 2,
      title: 'Lorem ipsum dolor sit amet',
      instructor: 'Jane Smith',
      image: 'assets/images/course-placeholder.jpg',
      price: 12.99,
      isFree: false
    },
    {
      id: 3,
      title: 'Consectetur adipiscing elit',
      instructor: 'Robert Johnson',
      image: 'assets/images/course-placeholder.jpg',
      price: 0,
      isFree: true
    },
    {
      id: 4,
      title: 'Cras mattis consectetur',
      instructor: 'Emily Davis',
      image: 'assets/images/course-placeholder.jpg',
      price: 19.99,
      isFree: false
    }
  ];

  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = +params['id']; // Convertir a número
      if (id) {
        this.courseId = id;
        // En una implementación real, aquí se cargarían los datos del curso desde una API
      }
    });

    // Inicializar con la primera lección
    this.loadLesson(1);
  }

  loadLesson(lessonId: number): void {
    this.currentLesson = lessonId;
    const lesson = this.course.lessons.find(l => l.id === lessonId);
    
    if (lesson && lesson.videoUrl) {
      const newVideoUrlString = lesson.videoUrl;
      this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(newVideoUrlString);
    } else {
      this.videoUrl = null;
    }
  }

  toggleLessonList(): void {
    this.showLessonList = !this.showLessonList;
  }

  nextLesson(): void {
    const currentIndex = this.course.lessons.findIndex(l => l.id === this.currentLesson);
    if (currentIndex < this.course.lessons.length - 1) {
      this.loadLesson(this.course.lessons[currentIndex + 1].id);
    }
  }

  previousLesson(): void {
    const currentIndex = this.course.lessons.findIndex(l => l.id === this.currentLesson);
    if (currentIndex > 0) {
      this.loadLesson(this.course.lessons[currentIndex - 1].id);
    }
  }

  markLessonCompleted(lessonId: number): void {
    const lesson = this.course.lessons.find(l => l.id === lessonId);
    if (lesson) {
      lesson.completed = true;
      // En una implementación real, aquí se actualizaría el progreso en la API
    }
  }

  calculateProgress(): number {
    const completedLessons = this.course.lessons.filter(l => l.completed).length;
    return Math.round((completedLessons / this.course.lessons.length) * 100);
  }

  isLastLesson(): boolean {
    const currentIndex = this.course.lessons.findIndex(l => l.id === this.currentLesson);
    return currentIndex === this.course.lessons.length - 1;
  }

  isFirstLesson(): boolean {
    return this.currentLesson === this.course.lessons[0].id;
  }
} 